import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { meanBy, sumBy } from 'lodash';
import { groupBy } from 'lodash';
import { EmptyStateBox, IconWarning } from '../../../../../../components';
import {
  MeasurementChannelKind,
  MeasurementSubscriptionResponse,
  OptimizerScalarParameterTypeInternal,
  OptimizerVectorParameterTypeInternal,
} from '../../../../../../data-access/gql-types/graphql';
import { useMeasurementMapOperations } from '../../../../hooks/measurement-map/use-measurement-map-operations';
import { MeasurementItem, MeasurementGroup } from '../../measurements/components/measurement-item';
import { PhasesButtonGroup } from '../../measurements/components/phases-button-group';
import {
  averageSummaryMeasurements,
  filteredPhasesSumMeasurementsOptimizer,
  filteredSummaryMeasurements,
  measurementParametersOptimizer,
} from '../../measurements/utils';

type OptimizerMeasurementsProps = {
  channelId: string;
  measurementFullData: MeasurementSubscriptionResponse | undefined;
};

const kind = MeasurementChannelKind.Optimizer;

export const OptimizerMeasurements: React.FC<OptimizerMeasurementsProps> = ({ measurementFullData, channelId }) => {
  const { t } = useTranslation('channel-details');
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const { convertNumberToMeasurement } = useMeasurementMapOperations();

  const vectorMeasurements = measurementFullData?.vectorMeasurements || [];
  const scalarsMeasurements = measurementFullData?.scalarMeasurements || [];

  const phaseTabs = useMemo(() => {
    const phases = groupBy(vectorMeasurements, 'index');
    return Object.keys(phases).map((phase) => Number(phase));
  }, [vectorMeasurements]);

  const phaseMeasurementsGroupedByPhase = useMemo(() => groupBy(vectorMeasurements, 'index'), [vectorMeasurements]);

  const phaseMeasurementsGroupedByType = useMemo(() => groupBy(vectorMeasurements, 'type'), [vectorMeasurements]);

  if (!scalarsMeasurements.length && !vectorMeasurements.length) {
    return (
      <div className="p-t-16 p-l-24 p-r-24">
        <EmptyStateBox content={t('noDataToDisplay')} icon={<IconWarning />} />
      </div>
    );
  }

  return (
    <div className="list-measurement p-l-24 p-r-24">
      <div className="list-measurement__tab-container">
        <PhasesButtonGroup
          selectedPhases={[activeTabIndex]}
          onSelectedPhases={(phases) => setActiveTabIndex(phases[0])}
          defaultPhases={[0, ...(phaseTabs || [])]}
          className="m-t-0 m-b-16 phases-button-group--background-element"
          singleChoice
          itemPrefix={t('phase')}
        />
      </div>
      <div className="list-measurement__wrapper grid-list-16">
        {activeTabIndex === 0 ? (
          <>
            {Object.entries(phaseMeasurementsGroupedByType)
              ?.filter((phaseMeasurementGroupType) =>
                filteredPhasesSumMeasurementsOptimizer.includes(
                  convertNumberToMeasurement(kind)(
                    'vectors',
                    phaseMeasurementGroupType[0],
                  ) as OptimizerVectorParameterTypeInternal,
                ),
              )
              .map((phaseMeasurementGroupType) => {
                const type = convertNumberToMeasurement(kind)(
                  'vectors',
                  phaseMeasurementGroupType[0],
                ) as OptimizerVectorParameterTypeInternal;
                const value = averageSummaryMeasurements.includes(type)
                  ? meanBy(phaseMeasurementGroupType[1], 'value')
                  : sumBy(phaseMeasurementGroupType[1], 'value');

                if (measurementParametersOptimizer[type])
                  return (
                    <MeasurementItem
                      key={type}
                      channelId={channelId}
                      measurement={type}
                      measurementGroup={MeasurementGroup.SinglePhase}
                      text={t(`energyMeasurements.types.summary.${type}`)}
                      value={type !== OptimizerVectorParameterTypeInternal.ActivePower ? value : value / 1000}
                      parameters={measurementParametersOptimizer[type]}
                      kind={kind}
                    />
                  );
              })}
            {scalarsMeasurements
              ?.filter((absoluteMeasurement) =>
                filteredSummaryMeasurements.includes(
                  convertNumberToMeasurement(kind)(
                    'scalars',
                    absoluteMeasurement.type,
                  ) as OptimizerScalarParameterTypeInternal,
                ),
              )
              .map((absoluteMeasurement) => {
                const measurement = convertNumberToMeasurement(kind)(
                  'scalars',
                  absoluteMeasurement.type,
                ) as OptimizerScalarParameterTypeInternal;

                if (measurementParametersOptimizer[measurement]) {
                  return (
                    <MeasurementItem
                      key={absoluteMeasurement.type}
                      channelId={channelId}
                      measurement={measurement}
                      measurementGroup={MeasurementGroup.Absolute}
                      text={t(`energyMeasurements.types.summary.${measurement}`)}
                      value={absoluteMeasurement.value}
                      parameters={measurementParametersOptimizer[measurement]}
                      kind={kind}
                    />
                  );
                }
              })}
          </>
        ) : (
          phaseMeasurementsGroupedByPhase[activeTabIndex]?.map((phaseMeasurement) => {
            const measurement = convertNumberToMeasurement(kind)(
              'vectors',
              phaseMeasurement.type,
            ) as OptimizerVectorParameterTypeInternal;
            if (measurementParametersOptimizer[measurement]) {
              return (
                <MeasurementItem
                  key={phaseMeasurement.type}
                  channelId={channelId}
                  measurement={measurement}
                  measurementGroup={MeasurementGroup.SinglePhase}
                  text={t(
                    `energyMeasurements.types.phase.${convertNumberToMeasurement(kind)(
                      'vectors',
                      phaseMeasurement.type,
                    )}`,
                  )}
                  value={
                    measurement !== OptimizerVectorParameterTypeInternal.ActivePower
                      ? phaseMeasurement.value
                      : phaseMeasurement.value / 1000
                  }
                  parameters={measurementParametersOptimizer[measurement]}
                  phase={activeTabIndex}
                  kind={kind}
                />
              );
            }
          })
        )}
      </div>
    </div>
  );
};
